import React, { useState, useEffect } from 'react';
import { CheckCircle, ChevronDown } from 'lucide-react';
import { AI_STYLES, getStyle, getStyleOptions } from '../config/aiStyles';

const ProcessingOptions = ({
  selectedStyle,
  setSelectedStyle,
  descriptionSentences,
  setDescriptionSentences,
  getExampleMetadata
}) => {
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStyleDropdown && !event.target.closest('.style-dropdown')) {
        setShowStyleDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStyleDropdown]);
  // Emoji mapping for each AI style
  // Get all styles except shuffle for main grid
  const allStyles = Object.values(AI_STYLES).filter(style => style.key !== 'shuffle');
  
  return (
    <div className="mt-6 bg-white p-6 rounded-lg shadow">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">How do you want your photo descriptions?</h3>
        <p className="text-gray-600">Choose the style and detail level for your AI-generated titles, descriptions, and tags</p>
      </div>
        
      {/* Style Selection Dropdown */}
      <div className="mb-6">
        <label className="block text-lg font-semibold text-gray-800 mb-3">Choose Your Style</label>
        
        <div className="relative style-dropdown">
          <button
            type="button"
            onClick={() => setShowStyleDropdown(!showStyleDropdown)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">{getStyle(selectedStyle).shortTitle}</span>
                <span className="text-gray-500 ml-2">— {getStyle(selectedStyle).description}</span>
              </div>
              <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showStyleDropdown ? 'rotate-180' : ''}`} />
            </div>
          </button>
          
          {showStyleDropdown && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-auto">
              {allStyles.map(style => (
                <div
                  key={style.key}
                  onClick={() => {
                    setSelectedStyle(style.key);
                    setShowStyleDropdown(false);
                  }}
                  className={`cursor-pointer px-3 py-3 hover:bg-gray-50 border-l-4 transition-colors ${
                    selectedStyle === style.key 
                      ? `${style.selectedBorderColor} bg-blue-50` 
                      : 'border-transparent'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="text-lg mt-0.5">{style.emoji}</div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {style.shortTitle}
                        {style.popular && (
                          <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            Most Popular
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">{style.description}</div>
                      <div className="text-xs text-gray-500 mt-1">Best for: {style.bestFor}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Shuffle Option - Separate */}
        <div className="mt-4">
          <button
            onClick={() => setSelectedStyle('shuffle')}
            className={`w-full p-3 rounded-lg border-2 transition-all duration-200 ${
              selectedStyle === 'shuffle'
                ? `border-purple-500 bg-purple-50 shadow-md`
                : 'border-gray-200 hover:border-purple-300 hover:shadow-sm'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className="text-xl">{AI_STYLES.shuffle.emoji}</div>
              <div className="flex-1 text-left">
                <div className={`font-medium ${
                  selectedStyle === 'shuffle' ? 'text-purple-900' : 'text-gray-900'
                }`}>
                  Shuffle (Random Mix)
                </div>
                <div className="text-sm text-gray-600">Random style for each photo</div>
                <div className="text-xs text-purple-600 font-medium mt-1">Perfect for variety</div>
              </div>
              {selectedStyle === 'shuffle' && (
                <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
              )}
            </div>
          </button>
        </div>

        {/* Selected Style Description */}
        {selectedStyle && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-start space-x-3">
              <div className={`w-6 h-6 rounded-lg ${getStyle(selectedStyle).gradient} flex items-center justify-center text-sm`}>
                {AI_STYLES[selectedStyle].emoji}
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                  {getStyle(selectedStyle).title}
                  {getStyle(selectedStyle).popular && (
                    <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                      Most Popular
                    </span>
                  )}
                </h4>
                <p className="text-xs text-gray-600 mb-1">
                  <span className="font-medium">Best for:</span> {getStyle(selectedStyle).bestFor}
                </p>
                <p className="text-xs text-gray-700">
                  {getStyle(selectedStyle).longDescription}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Description Length */}
      <div className="mb-6">
        <label className="block text-lg font-semibold text-gray-800 mb-3">Choose Detail Level</label>
        
        <div className="flex items-center space-x-4">
          {/* Quick Select Buttons */}
          <div className="flex space-x-2">
            {[
              { value: 1, label: 'Short' },
              { value: 3, label: 'Medium' },
              { value: 5, label: 'Long' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setDescriptionSentences(option.value)}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  descriptionSentences === option.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          {/* Slider */}
          <div className="flex-1 max-w-32">
            <input
              type="range"
              min="1"
              max="5"
              value={descriptionSentences}
              onChange={(e) => setDescriptionSentences(parseInt(e.target.value))}
              className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          {/* Current Value */}
          <div className="text-sm text-gray-600 min-w-[4rem]">
            {descriptionSentences} sentence{descriptionSentences !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingOptions;
